import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { swallow } from "@/lib/log";
import { toast } from "sonner";

/**
 * Web Speech API microphone button.
 *
 *  - Prefers `webkitSpeechRecognition` (Safari/Chrome/Edge on iOS/mac/Android)
 *  - Locale defaults to en-US for maximum recognition-server support
 *  - Explicit permission errors → user-friendly toast telling them how to fix it
 *  - Emits `onTranscript(combinedText, isFinal)` continuously as speech is
 *    recognised and `onFinal(finalText)` once when the mic is stopped.
 */
export default function VoiceInput({ onTranscript, onFinal, size = "sm", className, testId = "voice-input-mic" }) {
  const [recording, setRecording] = useState(false);
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);
  const finalRef = useRef("");

  useEffect(() => {
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Rec) { setSupported(false); return; }
    const r = new Rec();
    r.continuous = true;
    r.interimResults = true;
    // en-US has the widest recognition-model support across browsers
    r.lang = "en-US";
    r.onstart = () => setRecording(true);
    r.onresult = (ev) => {
      let interim = "", finalText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) finalText += res[0].transcript + " ";
        else interim += res[0].transcript;
      }
      if (finalText) finalRef.current += finalText;
      const combined = (finalRef.current + interim).trim();
      onTranscript?.(combined, !!finalText);
    };
    r.onerror = (ev) => {
      // eslint-disable-next-line no-console
      console.warn("[VoiceInput] error:", ev.error);
      setRecording(false);
      switch (ev.error) {
        case "not-allowed":
        case "service-not-allowed":
          toast.error("Microphone blocked. Enable mic access in your browser/PWA settings.");
          break;
        case "audio-capture":
          toast.error("No microphone detected.");
          break;
        case "no-speech":
        case "aborted":
          // silent — just stop
          break;
        case "network":
          toast.error("Voice: network hiccup, try again.");
          break;
        default:
          toast.error(`Voice error: ${ev.error}`);
      }
    };
    r.onend = () => {
      setRecording(false);
      const val = finalRef.current.trim();
      if (val) onFinal?.(val);
      finalRef.current = "";
    };
    recognitionRef.current = r;
    return () => { try { r.stop(); } catch (e) { swallow("VoiceInput/cleanup")(e); } };
  }, []); // eslint-disable-line

  const toggle = async () => {
    if (!supported) {
      toast.error("Voice not supported on this browser. Try Chrome or Safari.");
      return;
    }
    const r = recognitionRef.current;
    if (!r) return;
    if (recording) { try { r.stop(); } catch { /* noop */ } return; }
    // Proactively request mic permission so the OS-level prompt appears reliably
    try {
      if (navigator?.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Immediately stop the tracks; we only wanted the permission prompt
        stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[VoiceInput] getUserMedia denied:", err?.name || err);
      toast.error("Microphone blocked. Enable mic access in your browser/PWA settings.");
      return;
    }
    try {
      finalRef.current = "";
      r.start();
      // r.onstart will flip recording=true — but as a fallback we set it here too
      setRecording(true);
    } catch (e) {
      // InvalidStateError → already started. Try to stop then restart.
      try { r.stop(); } catch { /* noop */ }
      setRecording(false);
    }
  };

  const sizeClass = size === "lg" ? "h-11 w-11" : size === "md" ? "h-10 w-10" : "h-9 w-9";

  return (
    <button
      type="button"
      onClick={toggle}
      className={cn(
        sizeClass,
        "rounded-full flex items-center justify-center transition border shrink-0",
        recording
          ? "bg-destructive text-destructive-foreground border-destructive animate-pulse ring-2 ring-destructive/40"
          : "bg-card border-border text-muted-foreground hover:bg-muted",
        !supported && "opacity-60",
        className,
      )}
      aria-label={recording ? "Stop recording" : "Start voice input"}
      data-testid={testId}
      title={supported ? (recording ? "Stop voice input" : "Start voice input") : "Voice not supported on this device"}
    >
      {recording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>
  );
}
